import { useState, useEffect, useMemo } from 'react';
import { api } from '../../../services/api';
import DataTable from '../componentes/DataTable';
import EditModal from '../componentes/EditModal';
import DeleteConfirm from '../componentes/DeleteConfirm';
import Badge from '../componentes/Badge';
import { Plus, CheckCircle2, Bell, Syringe, Stethoscope, CalendarClock } from 'lucide-react';

const ICONOS_EVENTO = {
  vacuna: Syringe,
  cita: CalendarClock,
  control: Stethoscope,
};

const formatearFecha = (f) => {
  if (!f) return '—';
  const d = new Date(f);
  return isNaN(d.getTime()) ? f : d.toLocaleDateString('es-AR');
};

const HistorialPanel = () => {
  const [historial, setHistorial] = useState([]);
  const [mascotas, setMascotas] = useState([]);
  const [contactos, setContactos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [search, setSearch] = useState('');

  // Carga conjunta
  const fetchHistorial = async () => {
    try {
      setLoading(true);
      const [histRes, mascRes, contRes] = await Promise.all([
        api.getHistorial(),
        api.getMascotas(),
        api.getContactos(),
      ]);
      setHistorial(histRes.data?.data || histRes.data || []);
      setMascotas(mascRes.data?.data || mascRes.data || []);
      setContactos(contRes.data?.data || contRes.data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHistorial(); }, []);

  // Mapas para resolución rápida
  const mascotaMap = useMemo(
    () => Object.fromEntries(mascotas.map((m) => [m.id, m])),
    [mascotas]
  );
  const contactoMap = useMemo(
    () => Object.fromEntries(contactos.map((c) => [c.id, c])),
    [contactos]
  );

  // Filtro de búsqueda: por nombre de mascota, título, tipo_evento, contacto (si existe)
  const filtered = useMemo(() => {
    if (!search.trim()) return historial;
    const term = search.toLowerCase();
    return historial.filter((h) => {
      const mascota = mascotaMap[h.id_mascota];
      const mascName = mascota?.nombre?.toLowerCase() || '';
      const titulo = h.titulo?.toLowerCase() || '';
      const tipo = h.tipo_evento?.toLowerCase() || '';
      const contacto = h.id_contacto ? contactoMap[h.id_contacto] : null;
      const contactoName = contacto
        ? `${contacto.nombre || ''} ${contacto.apellido || ''}`.toLowerCase()
        : '';
      return (
        mascName.includes(term) ||
        titulo.includes(term) ||
        tipo.includes(term) ||
        contactoName.includes(term) ||
        String(h.id_mascota).includes(term)
      );
    });
  }, [historial, search, mascotaMap, contactoMap]);

  const handleEdit = (h) => { setSelected({ ...h }); setShowEdit(true); };
  const handleCreate = () => { setSelected(null); setShowEdit(true); };
  const handleDelete = (id) => {
    setSelected(historial.find((h) => h.id === id));
    setShowDelete(true);
  };

  const handleSave = async (formData) => {
    try {
      if (selected) {
        await api.updateHistorial(selected.id, formData);
      } else {
        await api.createHistorial(formData);
      }
      setShowEdit(false);
      fetchHistorial();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar');
    }
  };

  const confirmDelete = async () => {
    try {
      await api.deleteHistorial(selected.id);
      setShowDelete(false);
      fetchHistorial();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  };

  // Opciones para selects del modal
  const mascotaOptions = mascotas.map((m) => ({ value: m.id, label: m.nombre }));
  const contactoOptions = [
    { value: '', label: 'Ninguno' },
    ...contactos.map((c) => ({
      value: c.id,
      label: `${c.nombre || ''} ${c.apellido || ''}`.trim() || `Contacto #${c.id}`,
    })),
  ];

  const tipoEventoOptions = [
    { value: 'vacuna', label: 'Vacuna' },
    { value: 'cita', label: 'Cita' },
    { value: 'control', label: 'Control' },
    { value: 'otro', label: 'Otro' },
  ];

  const columns = [
    {
      key: 'id_mascota',
      label: 'Mascota',
      id: 'col-mascota',
      render: (_, row) => {
        const mascota = mascotaMap[row.id_mascota];
        return mascota?.nombre || row.id_mascota;
      },
    },
    {
      key: 'titulo',
      label: 'Título',
      id: 'col-titulo',
      render: (v, row) => {
        const Icon = ICONOS_EVENTO[row.tipo_evento?.toLowerCase()] || CalendarClock;
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Icon size={15} style={{ color: 'var(--sp-primary)', flexShrink: 0 }} /> {v || '—'}
          </span>
        );
      },
    },
    { key: 'tipo_evento', label: 'Tipo', id: 'col-tipo' },
    { key: 'fecha_evento', label: 'Fecha', id: 'col-fecha', render: (v) => formatearFecha(v) },
    {
      key: 'completada',
      label: 'Completada',
      id: 'col-completada',
      render: (v) =>
        v == 1 ? (
          <Badge variant="success" icon={CheckCircle2}>Sí</Badge>
        ) : (
          <Badge variant="neutral">Pendiente</Badge>
        ),
    },
    {
      key: 'recordatorio',
      label: 'Recordatorio',
      id: 'col-recordatorio',
      render: (v) =>
        v == 1 ? (
          <Badge variant="info" icon={Bell}>Sí</Badge>
        ) : (
          <Badge variant="neutral">No</Badge>
        ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 800 }}>Historial / Agenda</h1>
        <button className="sp-btn sp-btn--primary" onClick={handleCreate}>
          <Plus size={18} /> Nuevo
        </button>
      </div>

      {/* Búsqueda */}
      <div style={{ marginBottom: '1rem' }}>
        <input
          className="sp-input"
          type="text"
          placeholder="Buscar por mascota, título, tipo, contacto…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: '380px' }}
        />
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        loading={loading}
        error={error}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {showEdit && (
        <EditModal
          title={selected ? 'Editar evento' : 'Crear evento'}
          fields={[
            { name: 'id_mascota', label: 'Mascota', type: 'select', options: mascotaOptions },
            { name: 'id_contacto', label: 'Contacto (opcional)', type: 'select', options: contactoOptions },
            { name: 'fecha_evento', label: 'Fecha evento', type: 'date' },
            { name: 'proxima_fecha', label: 'Próxima fecha', type: 'date' },
            { name: 'titulo', label: 'Título' },
            { name: 'nota', label: 'Nota', type: 'textarea' },
            { name: 'tipo_evento', label: 'Tipo', type: 'select', options: tipoEventoOptions },
            { name: 'laboratorio', label: 'Laboratorio' },
            { name: 'lote', label: 'Lote' },
            { name: 'notas', label: 'Notas adicionales', type: 'textarea' },
            { name: 'completada', label: 'Completada', type: 'checkbox' },
            { name: 'recordatorio', label: 'Recordatorio', type: 'checkbox' },
          ]}
          initialValues={selected || {}}
          onSave={handleSave}
          onCancel={() => setShowEdit(false)}
        />
      )}

      {showDelete && (
        <DeleteConfirm
          message={`¿Eliminar el evento "${selected.titulo}"?`}
          onConfirm={confirmDelete}
          onCancel={() => setShowDelete(false)}
        />
      )}
    </div>
  );
};

export default HistorialPanel;